// Imported from the module, not the barrel: the barrel pulls in every
// service, and a pure util has no business dragging that graph behind it.
import { FILTER_OPERATOR_TO_WIRE, RESULT_MODE_BY_OPERATION } from '../../../services/widgets.js';
import { OPERATORS_WITHOUT_VALUE } from '../constants/filterOperators.js';
import {
  AGGREGATE_WITHOUT_FIELD,
  ROW_COUNT_DATA_TYPE,
  WIDGET_CONTRACT_VERSION,
  WIDGET_FIELD_ROLES,
} from '../constants/widgetContract.js';
import { buildWidgetBindings } from './buildWidgetBindings.js';
import { buildColumnAliases } from './columnAliases.js';
import { buildQuerySources, sourceKeyFor } from './querySources.js';
import { assignSlotsFromDataStep } from './widgetSlotAssignments.js';
import { completeItems } from './slotAssignment.js';
import { readSlotModel } from './widgetSlotModel.js';
import { aggregateOutputType } from './widgetDataTypes.js';
import { shapeFieldKey, uniqueFieldKey } from './widgetFieldKey.js';
import { ROW_COUNT_REF, grouperRef, measureRef } from './widgetSlotPools.js';
import { buildNonFieldBindings } from './nonFieldBindings.js';
import { toDisplayConfig } from './displaySettings.js';

/**
 * The alias of the one aggregation the builder emits. Named once so the
 * definition and the presentation binding that points at it cannot drift.
 */
export const AGGREGATE_ALIAS = 'record_count';

/**
 * The alias a grouper is given in the query, by position.
 *
 * The presentation contract binds by **alias**, not by the entity's field code
 * — `field_key` is resolved against the query's own fields. Letting the backend
 * derive one and then guessing it is how every chart came back
 * `UI_PRESENTATION_BINDING_FIELD_MISSING` or a type mismatch against
 * `output_data_type: null`. Naming it here makes the binding deterministic: the
 * definition and the contract agree because the same function wrote both.
 *
 * Positional rather than derived from the field, because two groupers on the
 * same field are legal and would otherwise collide.
 */
export const grouperAlias = (index) => `dim_${index + 1}`;

/** The same, for an exposed column of a record set. */
export const columnAlias = (index) => `col_${index + 1}`;

/**
 * The alias the record uuid travels under.
 *
 * `row_key` accepts `UUID` and nothing else, and the only field that
 * qualifies is the entity's `<base>_uuid` — seeded `SYSTEM_CONTROL` and
 * hidden from forms, so the author never picks it.
 *
 * Since the rev4 engine landed (2026-10-20) the **backend** adds that column
 * and points the binding at it: the contract asks the front to send
 * `row_key: { field_key: 'record_uuid' }` and nothing else, and a widget read
 * back no longer lists a hidden `record_uuid` among its `recordSet.columns`.
 * So this is the name the two sides agreed on, not a column this file
 * appends any more (CT-5641).
 */
export const RECORD_KEY_ALIAS = 'record_uuid';

/**
 * One builder filter row → the filters the contract stores.
 *
 * A list, because one row is not always one filter. `between` has no operator
 * on the wire in any spelling, so the row it describes — a range — is sent as
 * the two the backend does have. The author wrote one rule and the query gets
 * one rule; only the encoding differs.
 *
 * The value goes in `literal`. `FilterValueSchema` is a tagged value —
 * `LITERAL` as opposed to a context variable or something asked of the end
 * user at runtime — and its field is `literal`. Sending `value` instead put
 * the whole thing past an extra key the model ignores: `create` answered
 * `DSW000` and stored `literal: null`, so every filter ever saved narrowed
 * nothing at all.
 *
 * @param {object} filter A complete row from `useWidgetBuilderForm`.
 * @returns {Array<object>} One entry, or two for a range.
 */
export const wireFilters = (filter) => {
  const fieldUuid = filter.fieldUuid || filter.column;
  const literal = (operator, value) => ({
    fieldUuid,
    operator,
    value: { kind: 'LITERAL', literal: value },
  });

  if (filter.operator === 'between') {
    return [
      literal(FILTER_OPERATOR_TO_WIRE.greater_or_equal, filter.value),
      literal(FILTER_OPERATOR_TO_WIRE.less_or_equal, filter.valueTo),
    ];
  }

  const operator = FILTER_OPERATOR_TO_WIRE[filter.operator];
  // An operator with no wire name is a bug in the vocabulary, not something to
  // paper over: sending ours verbatim is what produced `Unsupported filter
  // operator` at create. A test asserts the two sets stay aligned.
  if (!operator) return [];

  // The operators that take no value send none: `IS_NULL` with a literal
  // attached reads as a comparison against the empty string.
  return [OPERATORS_WITHOUT_VALUE.includes(filter.operator)
    ? { fieldUuid, operator, value: { kind: 'LITERAL' } }
    : literal(operator, filter.value)];
};

/**
 * The author's answers → the pool items the binding builder places.
 *
 * The form stores one ref per slot (a column's field uuid, a measure's or a
 * grouper's row id — see `widgetSlotPools`), and every pool item here already
 * carries the alias the query sends it under. So this is a lookup, not a
 * translation: a ref that resolves to nothing — a column no longer exposed, a
 * measure the author deleted — is dropped, and the slot falls back to what
 * `assignSlotsFromDataStep` would have put there.
 *
 * Partial answers are passed through rather than refused wholesale: the
 * completeness rule belongs to `buildWidgetBindings`, which returns `null` for
 * the whole object when a required slot ends up unfilled.
 *
 * @param {object} assignments `{ [slotKey]: ref | ref[] }` from the form.
 * @param {Array<object>} pool Every item the Data step produced, each with a
 *   `ref`.
 * @returns {object} `{ [slotKey]: item | item[] }`, ready for
 *   `assignSlotsFromDataStep`.
 */
const resolveAuthoredSlots = (assignments, pool) => {
  const byRef = new Map(pool.filter((item) => item.ref).map((item) => [item.ref, item.item]));

  return Object.entries(assignments ?? {}).reduce((acc, [slot, value]) => {
    const refs = Array.isArray(value) ? value : [value];
    const items = refs.map((ref) => byRef.get(ref)).filter(Boolean);
    if (!items.length) return acc;
    return { ...acc, [slot]: Array.isArray(value) ? items : items[0] };
  }, {});
};

/**
 * Turn builder state into the `WidgetDefinition` body
 * `POST /v0/dashboard/widget/create|update` accepts (CT-5200 / CT-5183).
 *
 * The wire vocabulary is the Dashboard API's camelCase one — `typeCode`,
 * `dataSource`, `visualizationConfig`, `userFilters` — not the `snake_case`
 * the rest of the platform uses. A pure function with no React imports, so the
 * rules that matter are unit-testable on their own:
 *
 *   1. **No refresh field, ever** (decision 7). The contract has
 *      `refreshSeconds`; the design dropped the refresh-rate select, so nothing
 *      here emits one.
 *   2. **Incomplete filter rows are dropped** (CT-5193). An empty row is allowed
 *      to sit in the UI, but it must not reach the backend.
 *   3. **No appearance block.** The Appearance step was dropped from the design
 *      on 2026-09-08 (CT-5198 Won't Do), so nothing here emits accent, density,
 *      sort, pagination or search settings — there is no UI that sets them.
 *
 * Since 2026-09-25 the definition has real fields for most of the Data step —
 * `resultMode`, `summary.groupers`, `recordSet.columns`, and filters that
 * reference fields by uuid — so `visualizationConfig` is down to what the
 * contract genuinely has nowhere else: the preview size and the member entity
 * the picker showed.
 *
 * @param {object} form State from `useWidgetBuilderForm`.
 * @param {{ columns?: Array, typeSchema?: object|null, aggregateLabel?: string }} [options]
 *   Loaded column metadata — read for each field's alias, stored type and
 *   origin — and the selected type's `configSchema`, which says which slots
 *   the author was asked to fill.
 */
export function buildWidgetPayload(
  form,
  { columns = [], typeSchema = null, aggregateLabel = '' } = {},
) {
  const {
    name,
    description,
    type,
    previewSize,
    dataset,
    endUserFiltersEnabled,
    validFilters = [],
    validMeasures = [],
    slotAssignments = {},
    nonFieldBindings = {},
    displaySettings = {},
    datasetRef = null,
  } = form;

  // `null` is "nothing picked yet" since 2026-09-08, so an unconfigured record
  // set persists an empty list rather than silently every column.
  const exposedFieldUuids = (dataset.exposedColumns ?? [])
    .map((column) => (typeof column === 'string' ? column : column?.fieldUuid))
    .filter(Boolean);
  const isDataset = dataset.sourceType === 'dataset';
  const resultMode = RESULT_MODE_BY_OPERATION[dataset.operationType] ?? 'SUMMARY';
  const isRecordSet = resultMode === 'RECORD_SET';

  /*
   * One alias per exposed column, derived from the field's own code
   * (CT-5529). Built once and shared by the query, the bindings and the slot
   * resolution below — three places that have to agree, and used to agree
   * only because all three recomputed the same positional name.
   */
  const aliasByUuid = buildColumnAliases(
    exposedFieldUuids, columns, columnAlias, [AGGREGATE_ALIAS, RECORD_KEY_ALIAS],
  );

  /*
   * The query's source graph (multi-entity authoring).
   *
   * A field from a table other than the composite's primary is only accepted
   * when the query declares where it comes from. The origin rides on every
   * column, so the graph is derived from the fields themselves rather than
   * read back — which is what makes this work while
   * `GET /v0/dashboard/widget/{uuid}` still answers `sources: []`.
   *
   * `null` when every field is the root's: a single-entity widget sends the
   * same payload it sent before this existed.
   */
  const columnOf = (fieldUuid) => columns.find((column) => column.fieldUuid === fieldUuid) ?? null;
  const originOf = (fieldUuid) => columnOf(fieldUuid)?.origin;
  const referencedOrigins = [
    ...exposedFieldUuids,
    ...validFilters.map((filter) => filter.fieldUuid || filter.column),
    ...dataset.groupers.map((grouper) => grouper.fieldUuid || grouper.column),
    // A measure reads a column too, and one from a related table needs the
    // graph as much as an exposed one does.
    ...validMeasures.map((measure) => measure.fieldUuid || measure.column),
  ].filter(Boolean).map(originOf);

  const { sources, keyByEntityUuid } = buildQuerySources({
    // The composite's primary: what the entity picker stored as `entityRef`.
    rootEntityUuid: dataset.entityRef || dataset.sourceId || '',
    origins: referencedOrigins,
  });
  const keyOf = (fieldUuid) => sourceKeyFor(originOf(fieldUuid), keyByEntityUuid);

  /*
   * The presentation contract, in its canonical place:
   * `visualizationConfig.bindings`, which the BFF stores verbatim as
   * `mui_ui_widgets.visualization_schema` and the rev4 sync reads to derive
   * the WIDGET query (CT-5641).
   *
   * Each item is self-describing now — `field` names the DATASET column,
   * `aggregate` / `time_grain` say what is done to it, `field_key` names the
   * result. The three pools below are what the Data step collects; which
   * slot each one lands in is `assignSlotsFromDataStep`'s decision, read
   * from the type's own schema.
   *
   * ## Who names the column, by mode
   *
   * In `ENTITY` mode the backend already knows: it builds the dataset from
   * the `recordSet`/`summary` this payload sends, so it fills `field` and
   * `aggregate` behind the binding and asks only that **`field_key` be the
   * alias of the element the author chose** — `col_1`, `dim_1`,
   * `record_count`. Sending our own readable key there would name a column
   * the generated dataset does not have. So the source is left to the
   * backend (`emitSource: false`) and every assignment carries its alias as
   * the key.
   *
   * In `EXISTING_QUERY` mode nobody else can: the binding has to carry
   * `field` (the dataset's own alias) beside `field_key`. This builder does
   * not reach that branch yet — no endpoint publishes a dataset's columns,
   * so there are no aliases to name — and emits no contract rather than one
   * built on guesses. See `docs/widgets/rev4-contract-frontend.md`.
   */
  const dataTypeOf = (fieldUuid) => columnOf(fieldUuid)?.dataType ?? null;

  /*
    The measures the author chose, each under the alias its aggregation will
    carry — `sum_total`, `avg_salary`, and `record_count` for a row count,
    which is the backend's own default naming.

    `field` is that **alias**, not the column's: in this mode `field_key` has
    to name the element the generated dataset carries, and for a measure that
    element is the aggregation. `dataType` is what the operation *produces*,
    because that is what the slot checks — `SUM` over an integer is still an
    integer, and a money slot refuses it (`aggregateOutputType`).

    With no measure chosen the summary counts rows, which is what the builder
    sent before there was a picker and what most widgets want.
  */
  const measureAliases = new Set();
  const aliasForMeasure = (measure) => {
    if (measure.aggregate === AGGREGATE_WITHOUT_FIELD) return AGGREGATE_ALIAS;
    const code = columnOf(measure.fieldUuid || measure.column)?.internalName ?? '';
    const base = shapeFieldKey(`${measure.aggregate}_${code}`) || AGGREGATE_ALIAS;
    return uniqueFieldKey(base, measureAliases);
  };
  const authoredMeasures = isRecordSet ? [] : validMeasures.map((measure) => {
    const fieldUuid = measure.fieldUuid || measure.column;
    const alias = aliasForMeasure(measure);
    measureAliases.add(alias);
    return {
      alias,
      fieldUuid: measure.aggregate === AGGREGATE_WITHOUT_FIELD ? null : fieldUuid,
      aggregate: measure.aggregate,
      dataType: aggregateOutputType(measure.aggregate, dataTypeOf(fieldUuid)),
      label: measure.label || undefined,
    };
  });
  const countsRowsOnly = !isRecordSet && !authoredMeasures.length;
  const measures = isRecordSet ? [] : (countsRowsOnly
    ? [{
      field: AGGREGATE_ALIAS,
      aggregate: AGGREGATE_WITHOUT_FIELD,
      dataType: ROW_COUNT_DATA_TYPE,
      label: aggregateLabel || undefined,
    }]
    : authoredMeasures.map((measure) => ({
      field: measure.alias,
      aggregate: measure.aggregate,
      dataType: measure.dataType,
      label: measure.label,
    })));

  /* The groupers, in the order the author added them, each under its alias. */
  const grouperRows = isRecordSet ? [] : dataset.groupers
    .filter((grouper) => grouper.fieldUuid || grouper.column);
  const dimensions = grouperRows.map((grouper, index) => ({
    field: grouperAlias(index),
    dataType: dataTypeOf(grouper.fieldUuid || grouper.column),
  }));

  const exposedColumns = isRecordSet
    ? exposedFieldUuids.map((fieldUuid) => ({
      field: aliasByUuid.get(fieldUuid),
      dataType: dataTypeOf(fieldUuid),
    }))
    : [];

  /*
    Every pool item under the ref the form holds for it (see
    `widgetSlotPools`): a measure and a grouper by their row id, a column by
    its field uuid, and the implicit row count by the alias it travels under.
    One list, because a slot that accepts two roles — `CORRELATION.x` is
    `COLUMN|AGGREGATE` — is answered from either.
  */
  const refPool = [
    ...measures.map((item, index) => ({
      ref: countsRowsOnly ? ROW_COUNT_REF : measureRef(validMeasures[index]),
      item,
    })),
    ...dimensions.map((item, index) => ({ ref: grouperRef(grouperRows[index], index), item })),
    ...exposedColumns.map((item, index) => ({ ref: exposedFieldUuids[index], item })),
  ];

  /* What the author answered in Configure, in the vocabulary of the pools. */
  const authoredSlots = resolveAuthoredSlots(slotAssignments, refPool);

  /*
    The bindings that bind no field — the period and the mode enums — travel
    in the same object and are built from their own state (fix/widgets).
    `time.field` is the alias of the element the author pointed at, for the
    same reason every `field_key` is: it has to name a column the generated
    dataset carries.
  */
  /*
    `time.field` is a **source column**, which the schema is explicit about
    (`x-source: SOURCE_COLUMN`, *"no un field de la query"*), so what travels
    is the column's own code — not the alias the query gives it. A ref that
    resolves to no column falls back to itself: a period saved before its
    columns loaded carries the code it was stored under, and dropping it
    would quietly delete the widget's period on the next save.
  */
  const sourceKeyForRef = (ref) => columnOf(ref)?.internalName
    ?? refPool.find((entry) => entry.ref === ref)?.item?.field
    ?? ref ?? '';
  const nonField = buildNonFieldBindings(nonFieldBindings, sourceKeyForRef);

  /*
    ## The dataset-first flow (CT-5725)

    When the Data step published its selection as a DATASET, the widget is
    created **on top of it**: `EXISTING_QUERY` with its uuid, and bindings
    that carry `field` — the dataset's own `fieldKey` — beside `field_key`.
    The aggregations are the widget's, so they ride on the bindings and the
    dataset stays a plain record set.

    The three pools are the same three selections; only the name of each
    element changes, from the alias the backend would have generated to the
    alias the dataset already published.
  */
  const datasetColumns = datasetRef?.columns ?? [];
  const fieldKeyOf = (fieldUuid) => datasetColumns
    .find((column) => column.fieldUuid === fieldUuid)?.fieldKey ?? '';
  const datasetRowKey = datasetColumns.find((column) => column.isRowKey)?.fieldKey
    ?? RECORD_KEY_ALIAS;
  /*
    Both dataset paths are the same payload (CT-5860). The entity path
    publishes its selection and the author's own `Existing dataset` was
    published before they got here, and once `datasetRef` carries the column
    list there is no difference left: the bindings name published `fieldKey`s
    either way. It used to exclude `isDataset`, which is why picking a dataset
    produced a widget with **no bindings at all** — `UI_BINDING_REQUIRED` at
    save, if the seeded picker had ever offered a real one.
  */
  const onDataset = Boolean(datasetRef?.datasetUuid);

  /*
    On a dataset the slots *are* the configuration: each one carries the
    column the author picked and, on a measure slot, the operation to run
    over it. So the bindings are built straight from the answers — no pool,
    no heuristic — with `field` resolved to the column's published
    `fieldKey`.
  */
  const datasetAssignments = () => {
    const model = readSlotModel(typeSchema);
    /*
      A record key only means something while the rows are still rows. The
      moment one slot aggregates, the query groups and there is no record to
      key on — binding it anyway is a column outside the GROUP BY, which the
      engine refuses (`COMPILE_COLUMN_WITH_AGGREGATE {"alias":"record_uuid"}`,
      dev 2026-10-23, on a CORRELATION of two measures).
    */
    const aggregates = model.slots.some((slot) => completeItems(slotAssignments[slot.key], {
      needsAggregate: slot.roles.includes(WIDGET_FIELD_ROLES.AGGREGATE),
    }).some((item) => item.aggregate));

    return model.slots.reduce((acc, slot) => {
      if (slot.isRecordKey) {
        return isRecordSet && !aggregates
          ? { ...acc, [slot.key]: { field: datasetRowKey } }
          : acc;
      }
      const needsAggregate = slot.roles.includes(WIDGET_FIELD_ROLES.AGGREGATE);
      const items = completeItems(slotAssignments[slot.key], { needsAggregate })
        .map((item) => ({
          ...(item.aggregate === AGGREGATE_WITHOUT_FIELD
            ? {}
            : { field: fieldKeyOf(item.field) }),
          ...(item.aggregate ? { aggregate: item.aggregate } : {}),
          // The name the widget prints for this field. Only travels where the
          // slot declares it; without one the drawing falls back to the
          // query alias, which is how a KPI ends up titled `record_count`.
          ...(item.label ? { label: item.label } : {}),
        }))
        .filter((item) => item.field || item.aggregate === AGGREGATE_WITHOUT_FIELD);
      if (!items.length) return acc;
      return { ...acc, [slot.key]: slot.isArray ? items : items[0] };
    }, {});
  };

  // A chosen dataset whose columns have not been read yet has no aliases to
  // name, so it emits no contract rather than one built on guesses.
  const bindings = isDataset && !onDataset ? null : buildWidgetBindings({
    configSchema: typeSchema,
    assignments: onDataset ? datasetAssignments() : assignSlotsFromDataStep({
      configSchema: typeSchema,
      measures,
      dimensions,
      columns: exposedColumns,
      /*
        Always, for a record set, and never asked: the contract wants
        `row_key: { field_key: 'record_uuid' }` and the backend points it at
        the uuid column it adds itself. A summary has no record to key on.
      */
      recordKey: isRecordSet ? { field: RECORD_KEY_ALIAS } : null,
      authored: authoredSlots,
    }),
    nonField,
    /*
      Who names the column: on a dataset the front does, with the published
      `fieldKey`; in `ENTITY` mode the backend owns `field`/`aggregate` and
      `field_key` has to be the alias of the chosen element (see above).
    */
    emitSource: onDataset,
  });

  const display = toDisplayConfig(displaySettings);

  return {
    name: name.trim(),
    description: description.trim(),
    typeCode: type,
    sourceLabel: dataset.sourceLabel || undefined,
    // The enum the API validates: a dataset-backed widget reuses a stored
    // query, an entity-backed one is built from the entity itself.
    provenance: isDataset || onDataset ? 'EXISTING_DATASET' : 'ENTITY_BUILDER',
    dataSource: {
      mode: isDataset || onDataset ? 'EXISTING_QUERY' : 'ENTITY',
      queryUuid: (onDataset ? datasetRef.datasetUuid : dataset.datasetId) || undefined,
      /*
        A widget on a published dataset sends no entity: the selection is the
        dataset's, and the backend refuses a source that tries to be both.
      */
      entity: isDataset || onDataset
        ? undefined
        : {
          // The composite, not the member the picker showed: the endpoint
          // takes `compositeEntityUuid` and validates every field uuid against
          // it. The member's own fields are accepted — that is the bridge.
          compositeEntityUuid: dataset.compositeEntityUuid || undefined,
          resultMode,
          // Omitted entirely for a single-entity widget — see `querySources`.
          ...(sources ? { sources } : {}),
          filters: validFilters.flatMap(wireFilters)
            .map((filter) => ({ ...filter, sourceKey: keyOf(filter.fieldUuid) })),
          // Exactly one of the two is sent: the endpoint rejects a record set
          // with no columns, and a summary has no columns to send.
          ...(isRecordSet
            ? {
              recordSet: {
                // The columns the author chose, and only those. The record
                // uuid used to be appended here as a hidden one; the rev4
                // backend adds it itself and a widget read back no longer
                // lists it (verified on dev, 2026-10-20).
                columns: exposedFieldUuids.map((fieldUuid) => ({
                  fieldUuid,
                  alias: aliasByUuid.get(fieldUuid),
                  sourceKey: keyOf(fieldUuid),
                })),
              },
            }
            : {
              summary: {
                aggregations: countsRowsOnly
                  // Nothing chosen: the summary is the record count, as it
                  // was before the picker existed.
                  ? [{ function: 'COUNT_ROWS', distinct: false, alias: AGGREGATE_ALIAS }]
                  : authoredMeasures.map((measure) => ({
                    function: measure.aggregate,
                    distinct: measure.aggregate === 'COUNT_DISTINCT',
                    alias: measure.alias,
                    ...(measure.fieldUuid
                      ? { fieldUuid: measure.fieldUuid, sourceKey: keyOf(measure.fieldUuid) }
                      : {}),
                  })),
                groupers: dataset.groupers
                  .filter((grouper) => grouper.fieldUuid || grouper.column)
                  .map((grouper, index) => ({
                    fieldUuid: grouper.fieldUuid || grouper.column,
                    alias: grouperAlias(index),
                    sourceKey: keyOf(grouper.fieldUuid || grouper.column),
                  })),
              },
            }),
        },
    },
    visualizationConfig: {
      // `const: 2` on all 29 types. Optional, and sent so a stored
      // configuration says which contract wrote it.
      ...(bindings ? { contract_version: WIDGET_CONTRACT_VERSION } : {}),
      ...(bindings ? { bindings } : {}),
      /*
        The type's own look-and-feel options, beside its bindings and governed
        by the same schema (CT-5579).

        Omitted entirely when the author set nothing. No `display` property in
        any of the 28 types declares a `default`, so an absent one is the
        backend's to decide, and writing an empty object would claim an
        intent the author never expressed.
      */
      ...(display ? { display } : {}),
      // The preview size is the widget's size on a dashboard, not a
      // builder-only preference — it is persisted.
      size: previewSize,
      sourceType: dataset.sourceType || undefined,
      // The member entity the picker showed. The definition only stores the
      // composite, and `field/list` takes the member — without this, reopening
      // for edit would have no columns to offer.
      memberEntityUuid: dataset.entityRef || undefined,
      sourceLabel: dataset.sourceLabel || undefined,
      integration: dataset.sourceIntegration || undefined,
      datasetShape: dataset.datasetShape || undefined,
    },
    userFilters: {
      enabled: Boolean(endUserFiltersEnabled),
      // The end-user filter *exposures* are a CT-5183 follow-up: the builder
      // only has the on/off switch today, so nothing is exposed yet.
      exposures: [],
    },
  };
}

export default buildWidgetPayload;
